<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useId, watch } from "vue";
import AppPopover from "../../components/AppPopover.vue";
import Dropdown from "../../components/Dropdown.vue";
import FormField from "../../components/FormField.vue";
import LucideIcon from "../../components/icons/LucideIcon.vue";
import LoadingAnimation from "../../components/Loading.vue";
import OKCancelModal from "../../components/OKCancelModal.vue";
import StatusBanner from "../../components/StatusBanner.vue";
import {
  ALGORITHM_OPTIONS,
  algorithmLabel,
  type AlgorithmType,
} from "../../redistribution/models/AlgorithmType.ts";
import {
  DEMAND_SOURCE_OPTIONS,
  demandSourceLabel,
  type DemandSourceType,
} from "../../redistribution/models/DemandSourceType.ts";
import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import {
  generateDemoShippingPos,
  generateOptimizedShippingPos,
} from "../../services/apiClient.ts";
import { useBulkOrderCreationStore } from "../../stores/bulkOrderCreationStore.ts";
import { useModalStore } from "../../stores/modalStore.ts";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";
import {
  DEMO_SHIPPING_SCENARIO_ID,
  isDemoAlgorithmIdentifier,
} from "./demoShippingScenario.ts";
import type {
  OptimizedShippingPoDivision,
  OptimizedShippingPoResponse,
} from "./optimizedShippingPoGeneration.ts";
import {
  generateSuggestedShippingPos,
  type SortDirection,
  type SuggestedPoSortField,
} from "./shippingPoGeneration.ts";
import {
  SHIPPING_PO_LAYOUT_STORAGE_KEY,
  type ShippingPoLayout,
} from "./shippingPoSettings.ts";
import ShippingPoSettingsMenu from "./ShippingPoSettingsMenu.vue";
import SuggestedShippingPoPanel from "./SuggestedShippingPoPanel.vue";

const props = withDefaults(
  defineProps<{
    identifier: string;
    intermediateOrderId?: string | undefined;
    rows: BulkOrderCreationRow[];
    demoMode?: boolean;
    demoQuantityRows?: BulkOrderCreationRow[];
    warehouseOptions?: DropdownOption[];
    lookupLoading?: boolean;
    lookupValid?: boolean;
  }>(),
  {
    demoMode: false,
    warehouseOptions: () => [],
    lookupLoading: false,
    lookupValid: true,
  }
);
const store = useBulkOrderCreationStore();
const capacityInputId = useId();
const modalStore = useModalStore();
const algorithm = ref<AlgorithmType>("Milp");
const demandSource = ref<DemandSourceType>("InventoryFlow");
const horizon = defineModel<string>("horizon", { default: "30" });
const demoExecution = ref("algorithms");
const illustrative = computed(
  () => props.demoMode && demoExecution.value === "illustrative"
);
const result = ref<OptimizedShippingPoResponse>();
const preferenceError = ref("");
const layout = ref<ShippingPoLayout>(readLayout());
function readLayout(): ShippingPoLayout {
  try {
    const stored = globalThis.localStorage.getItem(
      SHIPPING_PO_LAYOUT_STORAGE_KEY
    );
    return stored === "rail" ? "rail" : "grid";
  } catch (error) {
    preferenceError.value = `Unable to read the saved PO display preference. Using Grid. ${error instanceof Error ? error.message : String(error)}`;
    return "grid";
  }
}
function selectLayout(value: ShippingPoLayout): void {
  layout.value = value;
  try {
    globalThis.localStorage.setItem(SHIPPING_PO_LAYOUT_STORAGE_KEY, value);
    preferenceError.value = "";
  } catch (error) {
    preferenceError.value = `PO display changed for this session, but could not be saved. ${error instanceof Error ? error.message : String(error)}`;
  }
}
const hasResult = computed(() => Boolean(result.value));
const sortField = ref<SuggestedPoSortField>("poNumber");
const sortDirection = ref<SortDirection>("ascending");
const nextPoNumber = ref(0);
const resultCapacity = ref(0);
const resultHorizon = ref(30);
const resultAlgorithm = ref<AlgorithmType>("Milp");
const resultIllustrative = ref(false);
const generationError = ref("");
const capacityError = ref("");
const loading = ref(false);
const hasEditsByDivision = ref<Record<string, boolean>>({});
const hasEdits = computed(() =>
  Object.values(hasEditsByDivision.value).some(Boolean)
);
let controller: AbortController | undefined;
let run = 0;
const horizonOptions = [30, 60, 90].map((days) => ({
  label: `${String(days)} days`,
  value: String(days),
}));

const generationDivisions = computed(() => {
  if (!result.value) return [];
  const totalCount = result.value.divisions.reduce(
    (count, partition) => count + partition.suggestedPos.length,
    0
  );
  let sequence = 0;
  return result.value.divisions.map((partition) => ({
    ...partition,
    suggestedPos: partition.suggestedPos.map((po) => ({
      ...po,
      sequence: ++sequence,
      totalCount,
    })),
  }));
});

watch(
  generationDivisions,
  (divisions) => {
    nextPoNumber.value = divisions.reduce(
      (count, partition) => count + partition.suggestedPos.length,
      0
    );
  },
  { immediate: true }
);

function allocatePoNumber(): number {
  nextPoNumber.value += 1;
  return nextPoNumber.value;
}

function selectSortField(field: SuggestedPoSortField): void {
  if (sortField.value === field) {
    sortDirection.value =
      sortDirection.value === "ascending" ? "descending" : "ascending";
    return;
  }
  sortField.value = field;
  sortDirection.value = "ascending";
}

function invalidate(): void {
  run += 1;
  controller?.abort();
  loading.value = false;
}

watch(
  () => [props.identifier, props.demoMode, props.intermediateOrderId],
  () => {
    invalidate();
    result.value = undefined;
    generationError.value = "";
    capacityError.value = "";
    hasEditsByDivision.value = {};
  }
);
watch(
  () => [
    algorithm.value,
    demandSource.value,
    horizon.value,
    store.cubeCapacityFt3,
    props.demoQuantityRows,
    props.rows,
    demoExecution.value,
  ],
  () => {
    if (loading.value) {
      invalidate();
      generationError.value =
        "Settings changed. Generate again to use the new settings.";
    }
  }
);
onBeforeUnmount(invalidate);
watch(
  () => props.lookupLoading,
  (lookupLoading) => {
    if (lookupLoading) {
      invalidate();
      result.value = undefined;
      hasEditsByDivision.value = {};
    }
  }
);

function divisionWarehouses(division: string): DropdownOption[] {
  if (props.demoMode) return props.warehouseOptions;
  return props.warehouseOptions.filter((option) => {
    const metadataDivision = option.metadata?.division;
    const optionDivision =
      typeof metadataDivision === "string"
        ? metadataDivision
        : (option.value.split("_")[0] ?? "");
    return optionDivision.trim().toUpperCase() === division.toUpperCase();
  });
}

function validateResult(response: OptimizedShippingPoResponse): void {
  if (!Array.isArray(response.divisions) || !Array.isArray(response.warnings)) {
    throw new TypeError(
      "Suggested PO generation returned an invalid response."
    );
  }
  const divisions = new Set<string>();
  for (const partition of response.divisions) {
    if (
      !/^[A-Z]{2}$/.test(partition.division) ||
      divisions.has(partition.division) ||
      !Array.isArray(partition.rows) ||
      !Array.isArray(partition.suggestedPos) ||
      !Array.isArray(partition.leftoverRows) ||
      !Array.isArray(partition.warnings) ||
      ![null, "Milp", "BruteForce"].includes(partition.engineUsed) ||
      (partition.enginesUsed !== undefined &&
        (!Array.isArray(partition.enginesUsed) ||
          partition.enginesUsed.some(
            (engine) => !["Milp", "BruteForce"].includes(engine)
          )))
    ) {
      throw new Error(
        "Suggested PO generation returned an invalid division result."
      );
    }
    divisions.add(partition.division);
    const allRows = [
      ...partition.rows,
      ...partition.leftoverRows,
      ...partition.suggestedPos.flatMap((po) => po.rows),
    ];
    if (allRows.some((row) => row.division !== partition.division)) {
      throw new Error("Suggested PO generation returned mixed-division rows.");
    }
  }
}

async function executeGeneration(): Promise<void> {
  const capacity = Number(store.cubeCapacityFt3);
  if (!Number.isFinite(capacity) || capacity <= 0) {
    capacityError.value = "Enter a PO cube capacity greater than 0.";
    return;
  }
  capacityError.value = "";
  generationError.value = "";
  invalidate();
  const currentRun = run;
  const requestController = new AbortController();
  const requestedHorizon = Number(horizon.value);
  const requestedAlgorithm = algorithm.value;
  const requestedIllustrative = illustrative.value;
  controller = requestController;
  loading.value = true;
  try {
    let response: OptimizedShippingPoResponse;
    if (illustrative.value) {
      response = {
        divisions: [
          {
            division: "Demo",
            rows: props.rows,
            ...generateSuggestedShippingPos(props.rows, capacity),
            engineUsed: null,
            demandSourceUsed: null,
            warnings: [
              "Demo uses the illustrative packer; no live optimization was run.",
            ],
          },
        ],
        warnings: [],
      };
    } else if (props.demoMode) {
      if (!isDemoAlgorithmIdentifier(props.identifier)) {
        throw new Error(
          "Real-engine demo supports bulk Z-77204 and IO-10482, IO-10483, IO-10484. Look up one of these orders, or select illustrative packing."
        );
      }
      response = await generateDemoShippingPos(
        {
          scenarioId: DEMO_SHIPPING_SCENARIO_ID,
          ...(props.intermediateOrderId
            ? { intermediateOrderId: props.intermediateOrderId }
            : {}),
          quantities: (props.demoQuantityRows ?? props.rows).map((row) => ({
            sku: row.sku,
            requestedUnits: row.requestedUnits,
          })),
          algorithm: algorithm.value,
          demandSource: demandSource.value,
          rateOfSaleDays: Number(horizon.value),
          poCube: capacity,
        },
        requestController.signal
      );
      validateResult(response);
    } else {
      response = await generateOptimizedShippingPos(
        {
          searchTerm: props.identifier.trim(),
          ...(props.intermediateOrderId
            ? { intermediateOrderId: props.intermediateOrderId }
            : {}),
          algorithm: algorithm.value,
          demandSource: demandSource.value,
          rateOfSaleDays: Number(horizon.value),
          poCube: capacity,
        },
        requestController.signal
      );
      validateResult(response);
    }
    if (currentRun !== run) return;
    result.value = response;
    resultCapacity.value = capacity;
    resultHorizon.value = requestedHorizon;
    resultAlgorithm.value = requestedAlgorithm;
    resultIllustrative.value = requestedIllustrative;
    hasEditsByDivision.value = {};
  } catch (error) {
    if (currentRun !== run) return;
    generationError.value =
      error instanceof Error
        ? error.message
        : "Suggested POs could not be generated. Please try again.";
  } finally {
    if (currentRun === run) loading.value = false;
  }
}

function generate(): void {
  if (loading.value || props.lookupLoading || !props.lookupValid) return;
  if (hasEdits.value) {
    const currentResult = result.value;
    modalStore.show(OKCancelModal, {
      dialogId: "replace-suggested-po-edits",
      width: 460,
      height: 230,
      componentProps: {
        title: "Replace edited Suggested POs?",
        message:
          "Generating again replaces all current suggested POs and manual edits. User-created POs will be discarded. Continue?",
        okButtonText: "Replace and generate",
        cancelButtonText: "Keep edits",
        onOk: () => {
          if (result.value === currentResult) return executeGeneration();
        },
      },
    });
    return;
  }
  void executeGeneration();
}

function engineSummary(partition: OptimizedShippingPoDivision): string {
  if (partition.enginesUsed && partition.enginesUsed.length > 1) {
    return `Engines used: ${partition.enginesUsed.map((engine) => algorithmLabel(engine)).join(", ")}`;
  }
  return partition.engineUsed
    ? `Engine used: ${algorithmLabel(partition.engineUsed)}`
    : "No redistribution engine was run.";
}

defineExpose({
  generate,
  hasResult,
  loading,
  layout,
  sortField,
  sortDirection,
  selectLayout,
  selectSortField,
});
</script>

<template>
  <div class="shipping-generation-workspace">
    <div class="bulk-order-creation-shipping-header">
      <slot name="header" />
      <div class="shipping-generation-header-actions">
        <form
          class="bulk-order-creation-shipping-controls"
          aria-label="Generate Suggested POs"
          @submit.prevent="generate"
        >
          <label class="shipping-generation-capacity" :for="capacityInputId">
            <span>PO Cube Capacity (ft³)</span>
            <input
              :id="capacityInputId"
              v-model="store.cubeCapacityFt3"
              type="text"
              class="textbox"
              placeholder="2300"
              aria-label="PO Cube Capacity (ft³)"
              :aria-invalid="Boolean(capacityError)"
              :disabled="loading"
              required
            />
          </label>
          <button
            type="submit"
            class="btn btn-primary"
            data-testid="generate-suggested-pos-button"
            :disabled="
              rows.length === 0 || lookupLoading || !lookupValid || loading
            "
            @click.prevent="generate"
          >
            Generate Suggested POs
          </button>
          <button
            v-if="loading"
            type="button"
            class="btn btn-secondary"
            @click="invalidate"
          >
            Cancel
          </button>
        </form>
        <ShippingPoSettingsMenu
          :disabled="loading"
          :layout="layout"
          :sort-field="sortField"
          :sort-direction="sortDirection"
          @layout="selectLayout"
          @sort="selectSortField"
        >
          <template #default="{ overlayTarget }">
            <div
              class="shipping-generation-settings"
              data-testid="shipping-po-generation-settings"
            >
              <FormField v-if="demoMode" label="Demo execution">
                <Dropdown
                  v-model="demoExecution"
                  aria-label="Demo execution"
                  :teleport-to="overlayTarget ?? 'body'"
                  :options="[
                    { label: 'Real algorithms (API)', value: 'algorithms' },
                    {
                      label: 'Illustrative packing (offline)',
                      value: 'illustrative',
                    },
                  ]"
                />
              </FormField>
              <FormField label="Algorithm">
                <template #label-action>
                  <AppPopover
                    v-if="result"
                    :teleport-to="overlayTarget ?? 'body'"
                    title="Algorithm and generation details"
                    placement="bottom"
                    :width="420"
                    :trigger="['click', 'focus']"
                    tooltip
                  >
                    <template #trigger="{ tooltipId }">
                      <button
                        type="button"
                        class="shipping-generation-help-button"
                        aria-label="Algorithm and generation details"
                        :aria-describedby="tooltipId"
                      >
                        <LucideIcon name="circle-help" :size="16" />
                      </button>
                    </template>
                    <div class="shipping-generation-help-content">
                      <p v-if="illustrative">
                        Demo only: illustrative packing. Algorithm and demand
                        controls apply to live lookup.
                      </p>
                      <p v-else-if="demoMode">
                        Synthetic demo data; real optimization engine. Three
                        Intermediate Orders share division DM. No live order,
                        pricing, inventory, or demand resources are accessed.
                      </p>
                      <p v-else>
                        Inventory optimization runs within each division before
                        pack-aware container formation. MILP retains feasible
                        results; local search is used only when no feasible MILP
                        result is available.
                      </p>
                      <p v-if="resultIllustrative">
                        Suggested POs below use illustrative packing, not the
                        approved prioritization or allocation algorithm.
                      </p>
                      <p v-else>
                        Inventory/shortage redistribution precedes best-fit
                        decreasing pack-aware container packing. Global
                        container optimality is not guaranteed.
                      </p>
                      <p v-if="resultIllustrative">
                        Illustrative packing does not guarantee a globally
                        minimum PO count.
                      </p>
                      <p v-else>
                        Live suggestions use source metadata and pack-aware
                        container formation. Redistribution engine usage is
                        reported above. Container packing does not claim a
                        globally minimum PO count.
                      </p>
                      <p v-if="demoMode">
                        Demo pricing uses a development FirstCost snapshot (SE,
                        2026-10-15), in USD per ordered SKU unit; no live
                        FirstCost lookup is performed.
                      </p>
                      <p v-else>
                        Each price is live FirstCost in USD per ordered SKU
                        unit, not per carton. Prices remain manually editable.
                      </p>
                      <p v-if="!demoMode">
                        Requested: {{ algorithmLabel(resultAlgorithm) }} ·
                        {{ resultHorizon }} days · {{ resultCapacity }} ft³ per
                        container
                      </p>
                      <p
                        v-for="(warning, index) in result.warnings"
                        :key="`result-${index}`"
                      >
                        {{ warning }}
                      </p>
                      <div
                        v-for="partition in generationDivisions"
                        :key="partition.division"
                      >
                        <strong>
                          {{
                            demoMode
                              ? `Demo suggestions · Division ${partition.division}`
                              : `Division ${partition.division}`
                          }}
                        </strong>
                        <p>
                          {{ engineSummary(partition) }}
                          <template v-if="partition.demandSourceUsed">
                            · Demand used:
                            {{ demandSourceLabel(partition.demandSourceUsed) }}
                          </template>
                        </p>
                        <p
                          v-for="(warning, index) in partition.warnings"
                          :key="`division-${partition.division}-${index}`"
                        >
                          {{ warning }}
                        </p>
                        <p v-if="partition.suggestedPos.length === 0">
                          {{
                            demoMode && rows.length === 0
                              ? "No SKUs have an Order quantity above zero."
                              : "No suggested POs fit this capacity."
                          }}
                        </p>
                      </div>
                      <p v-if="result.divisions.length === 0">
                        No eligible source rows were returned.
                      </p>
                    </div>
                  </AppPopover>
                </template>
                <Dropdown
                  v-model="algorithm"
                  aria-label="Algorithm"
                  :teleport-to="overlayTarget ?? 'body'"
                  :options="[...ALGORITHM_OPTIONS]"
                  :disabled="illustrative"
                />
              </FormField>
              <FormField label="Demand">
                <Dropdown
                  v-model="demandSource"
                  aria-label="Demand"
                  :teleport-to="overlayTarget ?? 'body'"
                  :options="[...DEMAND_SOURCE_OPTIONS]"
                  :disabled="illustrative"
                />
              </FormField>
              <FormField label="Demand horizon">
                <Dropdown
                  v-model="horizon"
                  aria-label="Demand horizon"
                  :teleport-to="overlayTarget ?? 'body'"
                  :options="horizonOptions"
                  :disabled="illustrative"
                />
              </FormField>
            </div>
          </template>
        </ShippingPoSettingsMenu>
      </div>
    </div>
    <StatusBanner v-if="preferenceError" variant="error" role="alert">{{
      preferenceError
    }}</StatusBanner>
    <p
      v-if="capacityError"
      class="bulk-order-creation-field-error"
      role="alert"
    >
      {{ capacityError }}
    </p>
    <StatusBanner v-if="generationError" variant="error" role="alert">{{
      generationError
    }}</StatusBanner>
    <LoadingAnimation
      v-if="loading"
      context-message="Generating Suggested Shipping POs..."
    />
    <template v-if="result">
      <section
        v-for="partition in generationDivisions"
        :key="partition.division"
        :aria-label="`Suggested POs for division ${partition.division}`"
        :inert="loading"
      >
        <SuggestedShippingPoPanel
          :id-prefix="`generation-${partition.division}`"
          :optimized="!resultIllustrative"
          :rows="partition.rows"
          :generation-result="partition"
          :cube-capacity-ft3="resultCapacity"
          :warehouse-options="divisionWarehouses(partition.division)"
          :division="partition.division"
          :demo-mode="demoMode"
          :sort-field="sortField"
          :sort-direction="sortDirection"
          :next-po-sequence="allocatePoNumber"
          :layout="layout"
          :show-layout-toggle="false"
          @update:has-edits="hasEditsByDivision[partition.division] = $event"
        />
      </section>
    </template>
    <p
      v-else-if="!loading && !generationError"
      class="bulk-order-creation-shipping-empty"
    >
      Suggested Shipping POs will appear here after generation.
    </p>
  </div>
</template>

<style scoped>
.shipping-generation-workspace {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.75rem;
}

.bulk-order-creation-shipping-controls {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.shipping-generation-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  max-width: 100%;
  margin-left: auto;
}

.shipping-generation-capacity {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  color: var(--text-primary, #172033);
  font-size: 0.75rem;
  text-align: left;
}

.shipping-generation-capacity input {
  flex: 0 1 160px;
  width: 160px;
  max-width: 160px;
  min-width: 60px;
  height: 32px;
  min-height: 32px;
  box-sizing: border-box;
  padding: 0.25rem 0.5rem;
}

.shipping-generation-capacity span {
  flex: 0 0 auto;
}

.shipping-generation-help-button {
  display: inline-flex;
  min-width: 1.5rem;
  min-height: 1.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: var(--action-primary, #1261a0);
}

.shipping-generation-help-button:focus-visible {
  outline: 2px solid currentcolor;
  outline-offset: 2px;
}

.shipping-generation-help-content p {
  margin: 0 0 0.5rem;
}

.shipping-generation-help-content p:last-child {
  margin-bottom: 0;
}

.bulk-order-creation-shipping-controls button {
  flex: 0 0 auto;
  height: 32px;
  min-height: 32px;
  box-sizing: border-box;
  padding: 0.25rem 0.75rem;
  white-space: nowrap;
}

@media (width <= 600px) {
  .bulk-order-creation-shipping-controls {
    flex-wrap: wrap;
  }
}

.bulk-order-creation-shipping-empty {
  margin: 0;
  color: var(--text-secondary, #5d6878);
  font-size: 0.78rem;
}
</style>
